import { useRef, useEffect } from 'react';

export function EmailEditor({ value = '', onChange }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = () => {
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        document.execCommand('insertLineBreak');
        e.preventDefault();
      }
    }
  };

  const formatText = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    editorRef.current.focus();
    handleInput();
  };
  const changeFontSize = (delta) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    if (range.collapsed) return;

    let container = selection.anchorNode;

    // Get nearest element node
    if (container.nodeType === 3) {
      container = container.parentElement;
    }

    // Look for closest font-size span
    const existingSpan = container.closest('span[style*="font-size"]');

    if (existingSpan) {
      // Modify existing span
      let currentSize = 100;

      const size = existingSpan.style.fontSize;

      if (size.includes('%')) {
        currentSize = parseInt(size);
      }

      let newSize = currentSize + delta;
      newSize = Math.min(200, Math.max(50, newSize));

      existingSpan.style.fontSize = `${newSize}%`;
      handleInput();
      return;
    }

    // Otherwise wrap selection
    const fragment = range.extractContents();

    const span = document.createElement('span');
    span.style.fontSize = `${100 + delta}%`;

    span.appendChild(fragment);
    range.insertNode(span);

    // Reselect content so user can press A+ again
    selection.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    selection.addRange(newRange);
  };


  const addLink = () => {
    const selection = document.getSelection();
    if (!selection.toString()) {
      alert('Please select some text to link');
      return;
    }

    const url = prompt('Enter URL:', 'https://');
    if (url) {
      let href = url.trim();
      // If no protocol is present
      if (!/^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(href)) {
        // If it contains '@', treat as email
        if (href.includes('@')) {
          href = 'mailto:' + href;
        } else {
          // Otherwise assume http://
          href = 'https://' + href;
        }
      }

      const range = selection.getRangeAt(0);
      const link = document.createElement('a');
      link.href = href;
      link.textContent = selection.toString();
      range.deleteContents();
      range.insertNode(link);

      editorRef.current.focus();
      handleInput();
    }
  };

  const editLink = () => {
    const selection = document.getSelection();
    const node = selection.anchorNode;
    const link = node.parentElement.closest('a');

    if (link) {
      const newUrl = prompt('Edit URL:', link.href);
      if (newUrl) {
        link.href = newUrl;
        handleInput();
      }
    } else {
      alert('Please click on a link to edit it');
    }
  };

  const addImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      const img = document.createElement('img');
      img.src = url;
      img.style.display = 'inline';
      img.style.maxHeight = '20px';
      img.style.verticalAlign = 'middle';
      img.style.margin = '0 2px';

      document.execCommand('insertHTML', false, img.outerHTML);
      editorRef.current.focus();
      handleInput();
    }
  };
  const handlePaste = (e) => {
    e.preventDefault();

    // Get plain text from clipboard
    const text = e.clipboardData.getData('text/plain');

    // Split by double line breaks (windows and unix style)
    const paragraphs = text.split(/\r?\n\s*\r?\n/);

    // Process each paragraph
    const html = paragraphs.map((paragraph, index) => {
      // Split single line breaks within paragraph
      const lines = paragraph.split(/\r?\n/);

      if (lines.length === 1) {
        return lines[0];
      } else {
        // Join lines with <br> tags
        return lines.join('<br>');
      }
    }).join('</p><p>'); // Join paragraphs with </p><p> tags

    // Wrap in <p> tags
    const finalHtml = `<p>${html}</p>`;

    // Insert the HTML
    document.execCommand('insertHTML', false, finalHtml);

    // Style images if present
    setTimeout(() => {
      const images = editorRef.current.querySelectorAll('img');
      images.forEach(img => {
        img.style.display = 'inline';
        img.style.maxHeight = '20px';
        img.style.verticalAlign = 'middle';
        img.style.margin = '0 2px';
      });
      handleInput();
    }, 0);
  };
  return (
    <>
      <style>{`
        .email-editor a {
          color: #2563eb !important;
          text-decoration: underline !important;
          cursor: pointer !important;
        }
        .email-editor a:hover {
          color: #1d4ed8 !important;
        }
        .email-editor img {
          display: inline !important;
          max-height: 20px !important;
          vertical-align: middle !important;
          margin: 0 2px !important;
        }
        .email-editor p {
          margin: 0 0 8px 0 !important;
          line-height: 1.5 !important;
        }
        .email-editor br {
          content: "" !important;
          display: block !important;
          margin-bottom: 4px !important;
        }
        
      `}</style>

      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-100 border-b p-2 flex gap-1 flex-wrap">
          <button
            type="button"
            onClick={() => formatText('bold')}
            className="px-3 py-2 bg-white border rounded hover:bg-gray-50 font-bold"
          >
            B
          </button>
          <button
            type="button"
            onClick={() => formatText('italic')}
            className="px-3 py-2 bg-white border rounded hover:bg-gray-50 italic"
          >
            I
          </button>
          <button
            type="button"
            onClick={() => formatText('underline')}
            className="px-3 py-2 bg-white border rounded hover:bg-gray-50 underline"
          >
            U
          </button>
          <input
            type="color"
            onChange={(e) => {
              document.execCommand('styleWithCSS', false, true);
              document.execCommand('foreColor', false, e.target.value);
              editorRef.current.focus();
              handleInput();
            }}
            className="w-10 h-11.5 p-1 bg-white border rounded hover:bg-gray-50 cursor-pointer"
            title="Text color"
          />
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => changeFontSize(-5)}
            className="px-3 py-2 bg-white border rounded hover:bg-gray-50 text-sm"
            title="Decrease font size"
          >
            A-
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => changeFontSize(5)}
            className="px-3 py-2 bg-white border rounded hover:bg-gray-50 text-lg"
            title="Increase font size"
          >
            A+
          </button>
          <button
            type="button"
            onClick={addLink}
            className="px-3 py-2 bg-white border rounded hover:bg-gray-50 text-blue-600"
          >
            🔗 Add
          </button>
          <button
            type="button"
            onClick={editLink}
            className="px-3 py-2 bg-white border rounded hover:bg-gray-50 text-green-600"
          >
            🔗 Edit
          </button>
          <button
            type="button"
            onClick={addImage}
            className="px-3 py-2 bg-white border rounded hover:bg-gray-50"
          >
            🖼️
          </button>
        </div>

        <div
          ref={editorRef}
          onPaste={handlePaste}
          contentEditable
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          className="email-editor min-h-[200px] p-4 focus:outline-none"
        />
      </div>
    </>
  );
}